import { formatPhoneNumber } from "react-phone-number-input"

import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import { Avatar, Button } from "@atoms/index"

import { ListItemStack } from "@molecules"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"

import EmailFlowDialog from "./EmailFlowDialog/EmailFlowDialog"
import PhoneFlowDialog from "./PhoneFlowDialog/PhoneFlowDialog"
import styles from "./UserContactsForms.module.scss"

export default function UserContactsForms() {
    const { user } = useUser()

    const hasVerifiedEmail = !!user?.email_verified
    const hasVerifiedPhone = !!user?.phone && !!user?.phone_verified
    const hasPhoneNumber = !!user?.phone

    return (
        <ListItemStack>
            <EmailFlowDialog>
                {({ onClick }: { onClick: () => void }) => (
                    <ListItem isClickable={true} onClick={onClick}>
                        <ListItem.AvatarAndContent
                            avatar={
                                !hasVerifiedEmail ? (
                                    <Avatar
                                        variant="object"
                                        size="xl"
                                        icon={faIcon({ name: "triangle-exclamation", style: "solid" })}
                                        baseClassName={styles.warningAvatarBackground}
                                        iconClassName={styles.warningAvatarIcon}
                                    />
                                ) : undefined
                            }
                            content={
                                <ListItemContent
                                    leftLabel={
                                        <ListItem.Label
                                            label="Email address"
                                            state={hasVerifiedEmail ? "default" : "warning"}
                                            showStateIcon={false}
                                        />
                                    }
                                    leftCaption={
                                        user?.email ? (
                                            <ListItem.Caption
                                                caption={hasVerifiedEmail ? user.email : `Verify ${user.email}`}
                                                state={hasVerifiedEmail ? "default" : "warning"}
                                                truncate={1}
                                            />
                                        ) : null
                                    }
                                />
                            }
                        />
                        <ListItem.Actions showChevron={true} />
                    </ListItem>
                )}
            </EmailFlowDialog>
            <ListItemDivider />
            <PhoneFlowDialog>
                {({ onClick }: { onClick: () => void }) =>
                    hasPhoneNumber ? (
                        <ListItem isClickable={true} onClick={onClick}>
                            <ListItem.AvatarAndContent
                                avatar={
                                    !hasVerifiedPhone ? (
                                        <Avatar
                                            variant="object"
                                            size="xl"
                                            icon={faIcon({ name: "triangle-exclamation", style: "solid" })}
                                            baseClassName={styles.warningAvatarBackground}
                                            iconClassName={styles.warningAvatarIcon}
                                        />
                                    ) : undefined
                                }
                                content={
                                    <ListItemContent
                                        leftLabel={
                                            <ListItem.Label
                                                label="Phone number"
                                                state={hasVerifiedPhone ? "default" : "warning"}
                                                showStateIcon={false}
                                            />
                                        }
                                        leftCaption={
                                            user?.phone ? (
                                                <ListItem.Caption
                                                    caption={
                                                        hasVerifiedPhone
                                                            ? formatPhoneNumber(user.phone)
                                                            : `Verify ${formatPhoneNumber(user.phone)}`
                                                    }
                                                    state={hasVerifiedPhone ? "default" : "warning"}
                                                    truncate={1}
                                                />
                                            ) : null
                                        }
                                    />
                                }
                            />
                            <ListItem.Actions showChevron={true} />
                        </ListItem>
                    ) : (
                        <ListItem>
                            <ListItem.AvatarAndContent
                                content={<ListItemContent leftLabel={<ListItem.Label label="Phone number" />} />}
                            />
                            <ListItem.Actions>
                                <Button onClick={onClick} size="lg" variant="subtle" isFullWidth={true}>
                                    Set phone number
                                </Button>
                            </ListItem.Actions>
                        </ListItem>
                    )
                }
            </PhoneFlowDialog>
        </ListItemStack>
    )
}
